import {
  useCurrentUserLevel,
  useTotalNegativeVideoReactions,
  useTotalNeutralVideoReactions,
  useTotalPositiveVideoReactions,
  useTotalSecondsWatchedOfVideoAllVideos,
} from "@/store/selectors/userProfileSelectors";
import { Box, Group } from "@mantine/core";
import { Text } from "./ui/Text";
import { useMemo, useEffect, useRef, useState, useCallback } from "react";
import { IconArrowRight, IconClock } from "@tabler/icons-react";
import {
  useStyles,
  useIsMobile,
  useIsLaptopOrBigger,
} from "@/styles/useStyles";
import { Button } from "./ui/Button";
import { reactionTypeToIcon, reactionTypeToColor } from "@/configs/reactions";
import { UserProfileLevel } from "./userProfile/UserProfileLevel";
import {
  useIsDemoApp,
  useIsPartnerApp,
} from "@/store/selectors/featureSelectors";
import { Analytics } from "@/services/Analytics";
import { useLocation } from "react-router-dom";

export function UserProfilePreview() {
  const { theme } = useStyles();
  const isMobile = useIsMobile();
  const { pathname } = useLocation();

  const totalSecondsWatched = useTotalSecondsWatchedOfVideoAllVideos();
  const totalPositiveVideoReactions = useTotalPositiveVideoReactions();
  const totalNeutralVideoReactions = useTotalNeutralVideoReactions();
  const totalNegativeVideoReactions = useTotalNegativeVideoReactions();
  const currentUserLevel = useCurrentUserLevel();
  const isLaptopOrBigger = useIsLaptopOrBigger();
  const isDemoApp = useIsDemoApp();
  const isPartnerApp = useIsPartnerApp();
  const isLoginPage = useMemo(() => {
    return pathname.includes("/login");
  }, [pathname]);

  const isAssignmentPage = useMemo(() => {
    return pathname.includes("/assignments");
  }, [pathname]);

  const minSecondsString = useMemo(() => {
    const minutes = Math.floor(totalSecondsWatched / 60);
    const seconds = Math.floor(totalSecondsWatched % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, [totalSecondsWatched]);

  const iconSize = isMobile ? 18 : 20;
  const textSize = isMobile ? "sm" : "md";

  const TotalTimeWatched = useMemo(() => {
    return (
      <Group gap={4} wrap="nowrap">
        <IconClock size={iconSize} color={theme.colors.primary[7]} />
        <Text c="primary.7" size={textSize} fw={700}>
          {minSecondsString}
        </Text>
      </Group>
    );
  }, [minSecondsString, theme.colors.primary, iconSize, textSize]);

  const TotalPositiveVideoReactions = (
    <ReactionCounter
      count={totalPositiveVideoReactions}
      reactionType="positive"
      iconSize={iconSize}
      textSize={textSize}
    />
  );

  const TotalNeutralVideoReactions = (
    <ReactionCounter
      count={totalNeutralVideoReactions}
      reactionType="neutral"
      iconSize={iconSize}
      textSize={textSize}
    />
  );

  const TotalNegativeVideoReactions = (
    <ReactionCounter
      count={totalNegativeVideoReactions}
      reactionType="negative"
      iconSize={iconSize}
      textSize={textSize}
    />
  );

  const buttonText = currentUserLevel?.buttonText || "View profile";

  const handleButtonClick = useCallback(
    (event: any) => {
      Analytics.click({
        action: "User level preview button",
        label: buttonText,
        event,
      });
    },
    [buttonText],
  );

  if (isPartnerApp || isLoginPage || isAssignmentPage) {
    return null;
  }

  return (
    <Group
      gap={isMobile ? "xs" : "md"}
      pos="fixed"
      bottom={isDemoApp ? 70 : 0}
      left={0}
      right={0}
      bg="primary.0"
      px={isMobile ? "xs" : "md"}
      py={isMobile ? "xs" : "sm"}
      justify="space-between"
      align="center"
      wrap={isMobile ? "wrap" : "nowrap"}
      style={{
        borderTop: `1px solid ${theme.colors.primary[1]}`,
        boxShadow: theme.shadows.md,
        zIndex: 100,
      }}
    >
      {/* Level info - centered on mobile, left on desktop */}
      <Group
        gap="xs"
        justify={isMobile ? "center" : "flex-start"}
        flex={1}
        style={{ flexShrink: 0, width: isMobile ? "100%" : "auto" }}
      >
        <UserProfileLevel />
      </Group>

      {/* Stats - centered, keep in single row on all screens */}
      <Group
        gap={isMobile ? "xs" : "lg"}
        flex={1}
        justify={isMobile ? "center" : isLaptopOrBigger ? "center" : "flex-end"}
        wrap="nowrap"
        style={{
          flex: isMobile ? 1 : 1,
          flexShrink: isMobile ? 1 : 0,
          minWidth: 0,
        }}
      >
        {TotalTimeWatched}
        {TotalPositiveVideoReactions}
        {TotalNeutralVideoReactions}
        {TotalNegativeVideoReactions}
      </Group>

      {/* Action button - full width on mobile, auto on desktop */}
      {isLaptopOrBigger && (
        <Group flex={1} justify="flex-end">
          <Button
            variant="filled"
            to={currentUserLevel?.buttonHref || "/profile"}
            size={isMobile ? "xs" : "sm"}
            color="primary"
            radius="xl"
            fullWidth={isMobile}
            onClick={handleButtonClick}
            rightSection={
              <IconArrowRight
                size={isMobile ? 16 : 20}
                color={theme.colors.text[1]}
              />
            }
            style={{
              flexShrink: 0,
              width: isMobile ? "100%" : "auto",
            }}
          >
            {buttonText}
          </Button>
        </Group>
      )}
    </Group>
  );
}

function ReactionCounter({
  count,
  reactionType,
  iconSize = 20,
  textSize = "md",
}: {
  count: number;
  reactionType: "positive" | "neutral" | "negative";
  iconSize?: number;
  textSize?: string;
}) {
  const { theme } = useStyles();
  const color = reactionTypeToColor[reactionType];
  const Icon = reactionTypeToIcon[reactionType];
  const prevCountRef = useRef(count);
  const [floatingIcons, setFloatingIcons] = useState<number[]>([]);

  useEffect(() => {
    if (count > prevCountRef.current) {
      const id = Date.now();
      setFloatingIcons((prev) => [...prev, id]);
      // Remove the floating icon after animation completes
      setTimeout(() => {
        setFloatingIcons((prev) => prev.filter((i) => i !== id));
      }, 500);
    }
    prevCountRef.current = count;
  }, [count]);

  return (
    <>
      <style>
        {`
          @keyframes floatUp {
            0% {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
            50% {
              opacity: 0.8;
              transform: translateY(-12px) scale(1.3);
            }
            100% {
              opacity: 0;
              transform: translateY(-24px) scale(0.8);
            }
          }
        `}
      </style>
      <Group gap={4} pos="relative" wrap="nowrap">
        <Box pos="relative" display="flex" style={{ flexShrink: 0 }}>
          <Icon size={iconSize} color={theme.colors[color][7]} />
          {floatingIcons.map((id) => (
            <Box
              key={id}
              pos="absolute"
              top={0}
              left={0}
              style={{
                animation: "floatUp 500ms ease-out forwards",
                pointerEvents: "none",
                display: "flex",
              }}
            >
              <Icon size={iconSize} color={theme.colors[color][7]} />
            </Box>
          ))}
        </Box>
        <Text
          c={`${color}.7`}
          size={textSize}
          fw={700}
          style={{ whiteSpace: "nowrap" }}
        >
          {count}
        </Text>
      </Group>
    </>
  );
}
